/**
 * "What it cost", on the clear after a metered pass (docs/efficiency-plan.md,
 * Phase 3 "In the product"): what the learner's agent spent against par, the
 * three costliest calls by size, and the one habit the ledger points to.
 * When that habit is the smallest model that works, it names the model par
 * was measured on, which passed every lab run of this one.
 *
 * It draws nothing at all unless every one of these holds, and that silence
 * is the design rather than a fallback:
 *
 * - the learner is signed in (the meter is per account);
 * - the meter shows feedback to this learner (`shown`; the study hides it
 *   from some people for a while, and the panel never says why);
 * - the latest closed attempt is this mission's, a pass, by an agent, and
 *   closed around now rather than on an earlier visit.
 *
 * A pass by hand shows nothing about cost: it has no figure, and a "$0.00"
 * beside it would read as the cheapest pass there is.
 *
 * The pass and the meter closing its attempt are two writes a moment apart,
 * so the first read can be early. It asks again a few times, briefly, and
 * the panel lands when the answer does, the way the certificate news above
 * it does. Nothing here ever had prompt text to show: the meter stores none.
 */
import { useEffect, useState } from 'react';
import type { AttemptFeedback } from '@engineer-os/engine';
import { feedbackForPass, fetchMeter, techniqueHref } from '../../lib/efficiency';
import { useStrings } from '../../state/useLocale';
import { useCost, useRatio } from '../efficiency/shared';
import { CostPractice } from './CostPractice';

/** Waits between reads while the meter catches up with the pass: about seven seconds in all. */
const RETRY_MS = [1200, 2500, 3500];
/** An attempt that closed longer ago than this is an earlier visit's, not this pass. */
const RECENT_MS = 5 * 60_000;

export function WhatItCost({ open, missionId, signedIn }: { open: boolean; missionId: string; signedIn: boolean }) {
  const feedback = usePassFeedback(open, missionId, signedIn);
  return feedback ? <CostPanel feedback={feedback} /> : null;
}

/** This pass's feedback while the clear is open, kept current as the agent's closing calls land (TwoRuns.tsx reads it too). */
export function usePassFeedback(open: boolean, missionId: string, signedIn: boolean): AttemptFeedback | null {
  const [feedback, setFeedback] = useState<AttemptFeedback | null>(null);

  useEffect(() => {
    setFeedback(null);
    if (!open || !signedIn) return;
    let live = true;
    let timer = 0;
    let tries = 0;
    const since = Date.now() - RECENT_MS;
    // The gateway can attribute the agent's closing call for two minutes.
    // Keep the private comparison current while the clear panel stays open.
    const settleUntil = Date.now() + 2 * 60_000;
    const read = async () => {
      const meter = await fetchMeter();
      if (!live) return;
      const found = feedbackForPass(meter, missionId, since);
      if (found) {
        setFeedback(found);
        if (Date.now() < settleUntil) timer = window.setTimeout(() => void read(), 5000);
        return;
      }
      // No meter here, or feedback hidden for this learner: asking again
      // would get the same answer.
      if (!meter?.shown || tries >= RETRY_MS.length) return;
      timer = window.setTimeout(() => void read(), RETRY_MS[tries++]);
    };
    void read();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [open, signedIn, missionId]);

  return feedback;
}

/** The panel itself, from feedback in hand: rendered straight to markup for the screenshots. */
export function CostPanel({ feedback }: { feedback: AttemptFeedback }) {
  const s = useStrings().efficiency;
  const cost = useCost();
  const ratio = useRatio();
  const { attempt, parMicro, costliest, technique, parModel } = feedback;
  const lesson = technique ? s.techniques[technique] : null;

  return (
    <section className="clear-cost" aria-label={s.clear.heading}>
      <h3 className="label">{s.clear.heading}</h3>
      <dl className="clear-cost-sums">
        <div>
          <dt>{s.clear.yours}</dt>
          <dd className="clear-cost-figure">{cost(attempt.costMicro)}</dd>
          <dd className="clear-cost-sub">{s.calls(attempt.calls)}</dd>
        </div>
        {parMicro !== null && (
          <div>
            <dt>{s.clear.par}</dt>
            <dd className="clear-cost-figure">{cost(parMicro)}</dd>
          </div>
        )}
        {feedback.ratio !== null && (
          <div className="clear-cost-ratio">
            <dt>{s.costs.vsPar}</dt>
            <dd className="clear-cost-figure">{ratio(feedback.ratio)}</dd>
          </div>
        )}
      </dl>
      {parMicro === null && <p className="note">{s.clear.noPar}</p>}
      {feedback.attempt.reported && <p className="note">{s.clear.reported}</p>}

      {costliest.length > 0 && (
        <>
          <h4 className="label clear-cost-h4">{s.clear.costliest}</h4>
          <ol className="clear-cost-calls">
            {costliest.map((call, i) => (
              <li key={`${call.at}-${i}`}>
                {/* A model id is machine text and reads left to right in
                    every language (docs/i18n.md §7). */}
                <span className="clear-cost-model machine" dir="ltr">
                  {call.model}
                </span>
                <span className="clear-cost-tokens">{s.clear.tokens(call.inputTokens, call.outputTokens)}</span>
                <span className="clear-cost-each">{cost(call.bookCostMicro)}</span>
              </li>
            ))}
          </ol>
        </>
      )}

      {feedback.practice && <CostPractice comparison={feedback.practice} missionId={attempt.ref} />}

      {technique && lesson && (
        <div className="clear-cost-try">
          <h4 className="label clear-cost-h4">{s.clear.tryThis}</h4>
          <p className="clear-cost-name">{lesson.name}</p>
          <p className="note">
            {lesson.line}{' '}
            {/* A new tab: the level behind this panel is still running. */}
            <a href={techniqueHref(technique)} target="_blank" rel="noopener">
              {s.clear.how}
            </a>
          </p>
          {technique === 'right-size-model' && parModel ? <p className="note">{s.clear.tryModel(parModel)}</p> : null}
        </div>
      )}
    </section>
  );
}

export default WhatItCost;
